import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Tooltip } from 'antd';
import { EditOutlined, UserDeleteOutlined } from '@ant-design/icons';
import userService from '../services/userService';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

const AdminUsersList = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });
    const [filters, setFilters] = useState({
        email: '',
        full_name: '',
        is_active: null
    });

    useEffect(() => {
        const fetchAdminUsers = async () => {
            try {
                setLoading(true);
                const params = {
                    page: pagination.page,
                    limit: pagination.limit,
                    filters: {
                        ...filters,
                        is_active: filters.is_active === 'Active' ? true : filters.is_active === 'Inactive' ? false : null,
                        role: "ADMIN"  // Only show ADMIN users
                    }
                };
                
                const { data, pagination: paginationData } = await userService.getUsers(params);
                setUsers(data);
                setPagination(prev => ({
                    ...prev,
                    total: paginationData.total,
                    totalPages: paginationData.totalPages
                }));
            } catch (error) {
                console.error('Error fetching admin users:', error);
                toast.error('Failed to load admin users. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchAdminUsers();
    }, [pagination.page, pagination.limit, filters]);

    const handlePageChange = (newPage) => {
        setPagination(prev => ({
            ...prev,
            page: Math.max(1, Math.min(newPage, pagination.totalPages))
        }));
    };

    const handleLimitChange = (e) => {
        setPagination(prev => ({
            ...prev,
            limit: parseInt(e.target.value, 10),
            page: 1
        }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        const searchTerm = e.target.search.value.trim();
        setFilters(prev => ({
            ...prev,
            email: searchTerm,
            full_name: searchTerm
        }));
        setPagination(prev => ({
            ...prev,
            page: 1
        }));
    };

    const handleStatusFilter = (e) => {
        const status = e.target.value === 'Select Status' ? null : e.target.value;
        setFilters(prev => ({
            ...prev,
            is_active: status
        }));
        setPagination(prev => ({
            ...prev,
            page: 1
        }));
    };

    const handleEditUser = (userId) => {
        navigate(`/admin/users/edit/${userId}`);
    };

    const toggleUserStatus = async (userId, currentStatus) => {
        try {
            // Update the UI optimistically
            setUsers(prevUsers => 
                prevUsers.map(user => 
                    user.id === userId 
                        ? { ...user, is_active: !currentStatus } 
                        : user
                )
            );
            
            // Make the API call
            await userService.toggleUserStatus(userId, !currentStatus);
            
            // Refresh the data to ensure consistency
            setPagination(prev => ({ ...prev }));
            toast.success('Admin user status updated successfully');
        } catch (error) {
            // Revert the UI on error
            setPagination(prev => ({ ...prev }));
            console.error('Error toggling admin user status:', error);
            toast.error('Failed to update admin user status');
        }
    };

    const formatDate = (dateString) => {
        try {
            return dateString ? format(new Date(dateString), 'dd MMM yyyy') : 'N/A';
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'N/A';
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
                    <select 
                        className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px" 
                        value={pagination.limit}
                        onChange={handleLimitChange}
                    >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    <form className="navbar-search" onSubmit={handleSearch}>
                        <input
                            type="text"
                            className="bg-base h-40-px w-auto"
                            name="search"
                            placeholder="Search by name or email"
                            defaultValue={filters.email || filters.full_name || ''}
                        />
                        <button type="submit" className="search-button">
                            <Icon icon="ion:search-outline" className="icon" />
                        </button>
                    </form>
                    <select 
                        className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px" 
                        value={filters.is_active === true ? 'Active' : filters.is_active === false ? 'Inactive' : 'Select Status'}
                        onChange={handleStatusFilter}
                    >
                        <option value="Select Status" disabled>Select Status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                    </select>
                </div>
                <Link
                    to="/admin/users/add"
                    className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                >
                    <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                    Add Admin
                </Link>
            </div>
            <div className="card-body p-24">
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="table-responsive scroll-sm">
                        <table className="table bordered-table sm-table mb-0">
                            <thead>
                                <tr>
                                    <th scope="col" className="text-nowrap">S.L</th>
                                    <th scope="col" className="text-nowrap">Join Date</th>
                                    <th scope="col">Name</th>
                                    <th scope="col">Email</th>
                                    <th scope="col">Role</th>
                                    <th scope="col" className="text-center">
                                        Status
                                    </th>
                                    <th scope="col" className="text-center">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.length > 0 ? (
                                    users.map((user, index) => (
                                        <tr key={user.id}>
                                            <td>
                                                {(pagination.page - 1) * pagination.limit + index + 1}
                                            </td>
                                            <td className="text-nowrap">
                                                {formatDate(user.created_at) || 'N/A'}
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="avatar avatar-sm me-2">
                                                        <div className="avatar-initial bg-soft-primary text-primary rounded-circle">
                                                            {user.full_name ? user.full_name.charAt(0).toUpperCase() : 'A'}
                                                        </div>
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <span className="text-md mb-0 fw-normal text-secondary-light">
                                                            {user.full_name || 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-nowrap">
                                                <span className="text-md mb-0 fw-normal text-secondary-light">
                                                    {user.email}
                                                </span>
                                            </td>
                                            <td className="text-capitalize">
                                                {user.role?.toLowerCase() || 'admin'}
                                            </td>
                                            <td className="text-center">
                                                <span 
                                                    className={`px-3 py-1 rounded-pill fw-medium text-xs ${user.is_active 
                                                        ? 'bg-success-focus text-success-600' 
                                                        : 'bg-danger-focus text-danger-600'}`}
                                                >
                                                    {user.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex align-items-center gap-2 justify-content-center">
                                                    <Tooltip title="Edit">
                                                        <Button
                                                            type="link" 
                                                            icon={<EditOutlined />}
                                                            className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-30-px h-30-px d-flex justify-content-center align-items-center rounded-circle"
                                                            onClick={() => handleEditUser(user.id)}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title={user.is_active ? 'Deactivate Admin' : 'Activate Admin'}>
                                                        <Button 
                                                            type="link" 
                                                            icon={<UserDeleteOutlined />}
                                                            className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-30-px h-30-px d-flex justify-content-center align-items-center rounded-circle"
                                                            onClick={() => toggleUserStatus(user.id, user.is_active)}
                                                        />
                                                    </Tooltip>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-4">
                                            <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                                <Icon icon="mdi:account-search" className="fs-1 text-muted mb-2" />
                                                <h5 className="mb-1">No admin users found</h5>
                                                <p className="text-muted mb-0">
                                                    {Object.values(filters).some(Boolean) 
                                                        ? 'Try adjusting your search or filter criteria' 
                                                        : 'No admin users available at the moment'}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
                {pagination.total > 0 && (
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                        <div>
                            Showing <span className="fw-semibold">
                                {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)}
                            </span> to <span className="fw-semibold">
                                {Math.min(pagination.page * pagination.limit, pagination.total)}
                            </span> of <span className="fw-semibold">{pagination.total}</span> entries
                            {Object.values(filters).some(Boolean) && (
                                <span className="ms-2">
                                    (filtered from {pagination.total} total entries)
                                </span>
                            )}
                        </div>
                        <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center mb-0">
                            <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px px-3"
                                    onClick={() => handlePageChange(1)}
                                    disabled={pagination.page === 1}
                                >
                                    <Icon icon="ep:d-arrow-left" />
                                </button>
                            </li>
                            <li className="page-item">
                                <button
                                    className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px px-3"
                                    onClick={() => handlePageChange(pagination.page - 1)}
                                    disabled={pagination.page === 1}
                                >
                                    <Icon icon="ep:arrow-left" />
                                </button>
                            </li>
                            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                let pageNum;
                                if (pagination.totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (pagination.page <= 3) {
                                    pageNum = i + 1;
                                } else if (pagination.page >= pagination.totalPages - 2) {
                                    pageNum = pagination.totalPages - 4 + i;
                                } else {
                                    pageNum = pagination.page - 2 + i;
                                }
                                return pageNum >= 1 && pageNum <= pagination.totalPages ? (
                                    <li key={pageNum} className="page-item d-none d-sm-block">
                                        <button
                                            className={`page-link fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                                                pagination.page === pageNum 
                                                    ? 'bg-primary-600 text-white' 
                                                    : 'bg-neutral-200 text-secondary-light'
                                            }`}
                                            onClick={() => handlePageChange(pageNum)}
                                        >
                                            {pageNum}
                                        </button>
                                    </li>
                                ) : null;
                            })}
                            <li className="page-item">
                                <button
                                    className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px px-3"
                                    onClick={() => handlePageChange(pagination.page + 1)}
                                    disabled={pagination.page >= pagination.totalPages}
                                >
                                    <Icon icon="ep:arrow-right" />
                                </button>
                            </li>
                            <li className={`page-item ${pagination.page >= pagination.totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px px-3"
                                    onClick={() => handlePageChange(pagination.totalPages)}
                                    disabled={pagination.page >= pagination.totalPages}
                                >
                                    <Icon icon="ep:d-arrow-right" />
                                </button>
                            </li>
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};
export default AdminUsersList;
